import { AlertTriangle, Download, FileSpreadsheet, Upload } from 'lucide-react';
import { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import type { ImportWorkbookResult, MaintenanceStats, ResetScope } from '@tt/shared';
import { Button } from '@/components/ui/button';
import {
  Alert,
  Badge,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  Spinner,
} from '@/components/ui/misc';
import {
  downloadWorkbook,
  useImportWorkbook,
  useMaintenanceStats,
  useResetOrganization,
} from '@/lib/admin-queries';
import { describeError } from '@/lib/errors';
import { AdminPage, ConfirmDialog } from './admin-ui';

/**
 * Data and maintenance: take the organization out as a spreadsheet, put one back, or empty it.
 *
 * The screen is ordered the way the work is: export first, because the sane sequence before
 * emptying anything is to have a copy of it.
 */
export function AdminMaintenancePage() {
  const { t } = useTranslation();
  const stats = useMaintenanceStats();

  return (
    <AdminPage title={t('admin.maintenance.title')} description={t('admin.maintenance.subtitle')}>
      {stats.isPending ? (
        <Spinner className="h-5 w-5" />
      ) : stats.isError ? (
        <Alert variant="error">{describeError(stats.error, t)}</Alert>
      ) : (
        <div className="space-y-4">
          <ExportCard stats={stats.data} />
          <ImportCard />
          <ResetCard stats={stats.data} />
        </div>
      )}
    </AdminPage>
  );
}

function ExportCard({ stats }: { stats: MaintenanceStats }) {
  const { t } = useTranslation();
  return (
    <Card>
      <CardHeader>
        <CardTitle>{t('admin.maintenance.exportTitle')}</CardTitle>
      </CardHeader>
      <CardContent className="space-y-3">
        <p className="text-sm text-muted-foreground">{t('admin.maintenance.exportBody')}</p>
        <dl className="flex flex-wrap gap-x-6 gap-y-1 text-sm">
          <Figure
            label={t('admin.maintenance.users')}
            value={stats.accounts.users + stats.accounts.retained}
          />
          <Figure label={t('admin.maintenance.groups')} value={stats.configuration.groups} />
          <Figure label={t('admin.maintenance.projects')} value={stats.configuration.projects} />
          <Figure label={t('admin.maintenance.entries')} value={stats.activity.timeEntries} />
        </dl>
        <Button onClick={() => downloadWorkbook()}>
          <Download className="h-4 w-4" aria-hidden="true" /> {t('admin.maintenance.exportAction')}
        </Button>
      </CardContent>
    </Card>
  );
}

function ImportCard() {
  const { t } = useTranslation();
  const input = useRef<HTMLInputElement>(null);
  const [file, setFile] = useState<File | null>(null);
  const [preview, setPreview] = useState<ImportWorkbookResult | null>(null);
  const [error, setError] = useState<string | null>(null);
  const importer = useImportWorkbook();

  const run = async (dryRun: boolean) => {
    if (!file) return;
    setError(null);
    try {
      const result = await importer.mutateAsync({ file, dryRun });
      setPreview(result);
      if (!dryRun) {
        toast.success(
          t('admin.maintenance.imported', {
            created: result.totals.created,
            updated: result.totals.updated,
          }),
        );
      }
    } catch (err) {
      setPreview(null);
      setError(describeError(err, t));
    }
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle>{t('admin.maintenance.importTitle')}</CardTitle>
      </CardHeader>
      <CardContent className="space-y-3">
        <p className="text-sm text-muted-foreground">{t('admin.maintenance.importBody')}</p>

        <div className="flex flex-wrap items-center gap-2">
          <input
            ref={input}
            type="file"
            accept=".xlsx,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
            aria-label={t('admin.maintenance.importFile')}
            className="text-sm file:mr-3 file:rounded-md file:border file:bg-muted file:px-3 file:py-1.5 file:text-sm"
            onChange={(e) => {
              setFile(e.target.files?.[0] ?? null);
              setPreview(null);
              setError(null);
            }}
          />
          <Button
            variant="outline"
            disabled={!file || importer.isPending}
            onClick={() => void run(true)}
          >
            <FileSpreadsheet className="h-4 w-4" aria-hidden="true" />{' '}
            {t('admin.maintenance.importPreview')}
          </Button>
          <Button
            disabled={!file || !preview || preview.totals.created + preview.totals.updated === 0}
            onClick={() => void run(false)}
          >
            <Upload className="h-4 w-4" aria-hidden="true" /> {t('admin.maintenance.importAction')}
          </Button>
          {importer.isPending ? <Spinner className="h-4 w-4" /> : null}
        </div>

        {error ? (
          <Alert variant="error" role="alert">
            {error}
          </Alert>
        ) : null}
        {preview ? <ImportPreview result={preview} /> : null}
      </CardContent>
    </Card>
  );
}

function ImportPreview({ result }: { result: ImportWorkbookResult }) {
  const { t } = useTranslation();
  const sheets = result.sheets.filter((s) => !s.missing);

  return (
    <div className="space-y-3">
      <p className="text-sm font-medium">
        {result.dryRun ? t('admin.maintenance.previewHeading') : t('admin.maintenance.doneHeading')}
      </p>
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <caption className="sr-only">{t('admin.maintenance.previewCaption')}</caption>
          <thead>
            <tr className="border-b text-left text-xs uppercase text-muted-foreground">
              <th scope="col" className="py-1.5 pr-4 font-medium">
                {t('admin.maintenance.sheet')}
              </th>
              <th scope="col" className="py-1.5 pr-4 text-right font-medium">
                {t('admin.maintenance.created')}
              </th>
              <th scope="col" className="py-1.5 pr-4 text-right font-medium">
                {t('admin.maintenance.updated')}
              </th>
              <th scope="col" className="py-1.5 pr-4 text-right font-medium">
                {t('admin.maintenance.unchanged')}
              </th>
              <th scope="col" className="py-1.5 text-right font-medium">
                {t('admin.maintenance.problems')}
              </th>
            </tr>
          </thead>
          <tbody>
            {sheets.map((sheet) => (
              <tr key={sheet.sheet} className="border-b last:border-0">
                <td className="py-1.5 pr-4">{sheet.sheet}</td>
                <td className="py-1.5 pr-4 text-right tabular-nums">{sheet.created}</td>
                <td className="py-1.5 pr-4 text-right tabular-nums">{sheet.updated}</td>
                <td className="py-1.5 pr-4 text-right tabular-nums text-muted-foreground">
                  {sheet.skipped}
                </td>
                <td className="py-1.5 text-right tabular-nums">
                  {sheet.errors.length ? (
                    <span className="text-destructive">{sheet.errors.length}</span>
                  ) : (
                    <span className="text-muted-foreground">0</span>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {sheets.some((s) => s.errors.length) ? (
        <div className="space-y-2">
          {sheets
            .filter((s) => s.errors.length)
            .map((sheet) => (
              <div key={sheet.sheet}>
                <p className="text-xs font-medium">{sheet.sheet}</p>
                <ul className="list-inside list-disc text-xs text-destructive">
                  {sheet.errors.slice(0, 20).map((issue, i) => (
                    <li key={`${issue.row}-${i}`}>
                      {t('admin.maintenance.rowError', { row: issue.row, message: issue.message })}
                    </li>
                  ))}
                </ul>
                {sheet.errors.length > 20 ? (
                  <p className="text-xs text-muted-foreground">
                    {t('admin.maintenance.moreProblems', { count: sheet.errors.length - 20 })}
                  </p>
                ) : null}
              </div>
            ))}
        </div>
      ) : null}
    </div>
  );
}

function ResetCard({ stats }: { stats: MaintenanceStats }) {
  const { t } = useTranslation();
  const [scopes, setScopes] = useState<ResetScope[]>([]);
  const [confirming, setConfirming] = useState(false);
  const reset = useResetOrganization();

  const toggle = (scope: ResetScope) =>
    setScopes((prev) =>
      prev.includes(scope) ? prev.filter((s) => s !== scope) : [...prev, scope],
    );

  // Accounts alone is refused by the server while entries exist; saying so here saves the trip.
  const accountsBlocked =
    scopes.includes('ACCOUNTS') && !scopes.includes('ACTIVITY') && stats.activity.timeEntries > 0;

  return (
    <Card className="border-destructive/40">
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <AlertTriangle className="h-4 w-4 text-destructive" aria-hidden="true" />
          {t('admin.maintenance.resetTitle')}
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <p className="text-sm text-muted-foreground">{t('admin.maintenance.resetBody')}</p>

        <fieldset className="space-y-3">
          <legend className="sr-only">{t('admin.maintenance.resetTitle')}</legend>
          <Choice
            checked={scopes.includes('ACTIVITY')}
            onChange={() => toggle('ACTIVITY')}
            title={t('admin.maintenance.activityTitle')}
            description={t('admin.maintenance.activityBody')}
            counts={[
              `${stats.activity.timeEntries} ${t('admin.maintenance.entries')}`,
              `${stats.activity.personalActivities} ${t('admin.maintenance.personalActivities')}`,
              `${stats.activity.reports} ${t('admin.maintenance.reports')}`,
            ]}
          />
          <Choice
            checked={scopes.includes('ACCOUNTS')}
            onChange={() => toggle('ACCOUNTS')}
            title={t('admin.maintenance.accountsTitle')}
            description={t('admin.maintenance.accountsBody')}
            counts={[
              `${stats.accounts.users} ${t('admin.maintenance.users')}`,
              `${stats.accounts.groupMemberships} ${t('admin.maintenance.memberships')}`,
            ]}
          />
        </fieldset>

        <Alert variant="info">
          {t('admin.maintenance.keeps', {
            groups: stats.configuration.groups,
            projects: stats.configuration.projects,
            activities: stats.configuration.sharedActivities,
          })}
        </Alert>

        {accountsBlocked ? (
          <Alert variant="warning" role="alert">
            {t('admin.maintenance.accountsBlocked', { count: stats.activity.timeEntries })}
          </Alert>
        ) : null}

        <Button
          variant="destructive"
          disabled={scopes.length === 0 || accountsBlocked || reset.isPending}
          onClick={() => setConfirming(true)}
        >
          {t('admin.maintenance.resetAction')}
        </Button>
      </CardContent>

      <ConfirmDialog
        open={confirming}
        onOpenChange={setConfirming}
        title={t('admin.maintenance.confirmTitle', { name: stats.organization.name })}
        description={
          <span>
            {scopes.includes('ACTIVITY') ? t('admin.maintenance.confirmActivity') + ' ' : ''}
            {scopes.includes('ACCOUNTS') ? t('admin.maintenance.confirmAccounts') + ' ' : ''}
            {t('admin.maintenance.confirmTail')}
          </span>
        }
        confirmLabel={t('admin.maintenance.resetAction')}
        confirmWord={stats.organization.name}
        onConfirm={() => {
          void reset.mutateAsync({ scopes, confirm: stats.organization.name }).then(
            (result) => {
              setScopes([]);
              toast.success(
                t('admin.maintenance.resetDone', {
                  entries: result.removed.timeEntries,
                  users: result.removed.users,
                }),
              );
            },
            (error: unknown) => toast.error(describeError(error, t)),
          );
        }}
      />
    </Card>
  );
}

function Choice({
  checked,
  onChange,
  title,
  description,
  counts,
}: {
  checked: boolean;
  onChange: () => void;
  title: string;
  description: string;
  counts: string[];
}) {
  return (
    <label className="flex gap-3 rounded-md border p-3 hover:bg-accent/40">
      <input type="checkbox" className="mt-1 h-4 w-4" checked={checked} onChange={onChange} />
      <span className="space-y-1">
        <span className="block font-medium">{title}</span>
        <span className="block text-sm text-muted-foreground">{description}</span>
        <span className="flex flex-wrap gap-1 pt-1">
          {counts.map((count) => (
            <Badge key={count}>{count}</Badge>
          ))}
        </span>
      </span>
    </label>
  );
}

function Figure({ label, value }: { label: string; value: number }) {
  return (
    <div className="flex items-baseline gap-1.5">
      <dt className="text-muted-foreground">{label}</dt>
      <dd className="font-medium tabular-nums">{value}</dd>
    </div>
  );
}
